import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  CircularProgress
} from "@mui/material";
import SurveillanceGrid from "./SurveillanceGrid";
import CashflowGrid from "./CashflowGrid";
import AnalyticsGrid from "./AnalyticsGrid";
import RunHistory from "./RunHistory";
import { ConfirmationDialog, isPermissionDenied, getPermissionDeniedMessage } from "@1823-partners/core";
import { getSurveillanceDates } from "../api";
import { useRunMonitor, RUN_TYPE_BOOK, RUN_TYPE_MONTHLY } from "../runs/useRunMonitor";

// Defined outside Dashboard: a component type created during render would be
// new on every render and remount the grids (dropping unsaved edits) each time
// Dashboard's state changes — which the run monitor now does as runs progress.
// `keepMounted` keeps a panel's children mounted (just hidden) while another
// tab is shown, so the Surveillance grid's unsaved edits survive a tab switch.
const TabPanel = ({ children, value, index, keepMounted = false }) => (
  <div role="tabpanel" hidden={value !== index}>
    {(keepMounted || value === index) && <Box>{children}</Box>}
  </div>
);

export const UNSAVED_DATE_CHANGE_MESSAGE =
  'You have unsaved surveillance edits for this date. Switching dates discards them. Switch anyway?';

const Dashboard = ({ userId }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [error, setError] = useState(null);

  // Load available dates
  const loadAvailableDates = useCallback(async () => {
    try {
      setIsLoadingDates(true);
      setError(null);

      const response = await getSurveillanceDates();
      const dates = Array.isArray(response.data) ? response.data : [];
      setAvailableDates(dates);

      // Auto-select the most recent date if available
      if (dates.length > 0 && !selectedDate) {
        const mostRecentDate = dates.sort((a, b) => new Date(b) - new Date(a))[0];
        setSelectedDate(mostRecentDate);
      }
    } catch (error) {
      console.error("Error fetching available dates:", error);
      if (isPermissionDenied(error)) {
        setError(getPermissionDeniedMessage(error));
      } else {
        setError("Failed to load available dates. Please try again.");
      }
    } finally {
      setIsLoadingDates(false);
    }
  }, [selectedDate]);

  // Load available dates on component mount
  useEffect(() => {
    loadAvailableDates();
  }, [loadAvailableDates]);

  // Bumped when run history should reload; completedRun tells the grid which
  // run just finished so it can refresh that date.
  const [historyRefreshToken, setHistoryRefreshToken] = useState(0);
  const [completedRun, setCompletedRun] = useState(null);

  const handleRunFinished = useCallback((run) => {
    setCompletedRun(run);
    setHistoryRefreshToken((token) => token + 1);
    // A finished run can add a date to the selector.
    loadAvailableDates();
  }, [loadAvailableDates]);

  const {
    activeRuns, isRunActive, runRequested, track, resumeFromHistory,
  } = useRunMonitor({ onRunFinished: handleRunFinished });
  const isSurveillanceRunning = isRunActive(RUN_TYPE_MONTHLY, selectedDate);
  const isBookRunning = isRunActive(RUN_TYPE_BOOK, selectedDate);

  // The request may or may not have been queued: reload history, whose
  // REQUESTED rows are picked up by resumeFromHistory.
  const handleRunOutcomeUnknown = useCallback(() => {
    setHistoryRefreshToken((token) => token + 1);
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // SurveillanceGrid reports whether it holds unsaved edits; a date switch
  // over them asks first instead of silently discarding them.
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);
  const [pendingDate, setPendingDate] = useState(null);

  const handleDateChange = useCallback((event) => {
    const newDate = event.target.value;
    if (newDate === selectedDate) return;
    if (hasUnsavedEdits) {
      setPendingDate(newDate);
      return;
    }
    setSelectedDate(newDate);
  }, [selectedDate, hasUnsavedEdits]);

  const handleConfirmDateChange = useCallback(() => {
    if (pendingDate !== null) setSelectedDate(pendingDate);
    setPendingDate(null);
  }, [pendingDate]);

  const handleCancelDateChange = useCallback(() => {
    setPendingDate(null);
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          mt: 4,
          mb: 3
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Surveillance Dashboard
        </Typography>
      </Box>

      {/* Date Selection */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, px: 2 }}>
        <TextField
          select
          label="Select Date"
          size="small"
          value={selectedDate}
          onChange={handleDateChange}
          disabled={isLoadingDates || availableDates.length === 0}
          sx={{ minWidth: 200 }}
          SelectProps={{
            native: true
          }}
          helperText={
            isLoadingDates
              ? "Loading dates..."
              : error
                ? error
                : availableDates.length === 0
                  ? "No dates available"
                  : "Select date to view data across all tabs"
          }
          error={!!error}
        >
          <option value="">Select Date</option>
          {availableDates.map((date) => (
            <option key={date} value={date}>
              {date}
            </option>
          ))}
        </TextField>
        {isLoadingDates && <CircularProgress size={24} sx={{ ml: 2, alignSelf: 'center' }} />}
      </Box>

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          centered
          sx={{
            '& .MuiTab-root': {
              minWidth: 120,
              fontWeight: 600,
            }
          }}
        >
          <Tab label="Surveillance" />
          <Tab label="Cashflow" />
          <Tab label="Analytics" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0} keepMounted>
        {/* Surveillance Grid */}
        <Box sx={{ px: 2 }}>
          <SurveillanceGrid
            userId={userId}
            isRunning={isSurveillanceRunning}
            isBookRunning={isBookRunning}
            onRunRequested={runRequested}
            onRunAlreadyActive={track}
            onRunOutcomeUnknown={handleRunOutcomeUnknown}
            completedRun={completedRun}
            selectedDate={selectedDate}
            onUnsavedChangesChange={setHasUnsavedEdits}
          />
        </Box>

        {/* Run History */}
        <Box sx={{ px: 2, mt: 4 }}>
          <RunHistory
            isRunning={activeRuns.size > 0}
            refreshToken={historyRefreshToken}
            onRowsLoaded={resumeFromHistory}
          />
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {/* Cashflow Grid */}
        <CashflowGrid selectedDate={selectedDate} />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        {/* Analytics Grid */}
        <AnalyticsGrid selectedDate={selectedDate} />
      </TabPanel>

      <ConfirmationDialog
        open={pendingDate !== null}
        onClose={handleCancelDateChange}
        onConfirm={handleConfirmDateChange}
        title="Discard unsaved edits?"
        message={UNSAVED_DATE_CHANGE_MESSAGE}
        confirmText="Discard and switch"
        confirmColor="warning"
      />
    </Box>
  );
};

export default Dashboard; 